import api from '@/redux/api/shipping';
import cloneDeep from 'lodash/cloneDeep';
import { createSelector } from '@reduxjs/toolkit';
import difference from 'lodash/difference';
import { getAuthToken } from '@/redux/modules/user';
import { getDeployment } from './config';
import { GlobalState } from '../rootReducer';
import ms from 'ms';
import union from 'lodash/union';

/* Max age of estimate in store */
const REDUX_STORE_TIME = ms('30m');

/* Action Types */
export const LOAD_SHIPPING_ESTIMATE_FAIL = 'la/domain/shippingEstimate/LOAD_FAIL';
export const LOAD_SHIPPING_ESTIMATE_REQUEST = 'la/domain/shippingEstimate/LOAD_REQUEST';
export const LOAD_SHIPPING_ESTIMATE_SUCCESS = 'la/domain/shippingEstimate/LOAD_SUCCESS';

/* Reducer */
export const DEFAULT_STATE = {
    byId: {},
    loaded: {},
    loading: [],
};

export type State = typeof DEFAULT_STATE;

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    let existing;
    let loaded;
    let loading;
    let time;

    switch (action.type) {
        case LOAD_SHIPPING_ESTIMATE_FAIL:
            existing = cloneDeep(state.byId);
            time = action.meta.actionTime;
            existing[action.meta.itemId] = 'error';
            loaded = cloneDeep(state.loaded);
            loaded[action.meta.itemId] = time;
            return {
                ...state,
                byId: existing,
                loaded,
                loading: difference(state.loading, [action.meta.itemId]),
            };
        case LOAD_SHIPPING_ESTIMATE_REQUEST:
            return {
                ...state,
                loading: union(state.loading, [action.payload.itemId]),
            };
        case LOAD_SHIPPING_ESTIMATE_SUCCESS:
            existing = cloneDeep(state.byId);
            loaded = cloneDeep(state.loaded);
            loading = cloneDeep(state.loading);
            time = action.meta.actionTime;
            existing[action.meta.itemId] = action.payload;
            loaded[action.meta.itemId] = time;
            return {
                ...state,
                byId: existing,
                loaded,
                loading: loading.filter((item) => item.itemId === action.meta.itemId),
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.shipping;
const idSelector = (state: GlobalState, id) => id;

const byIdSelector = createSelector(stateSelector, (state) => state.byId);

const loadedSelector = createSelector(stateSelector, (state) => state.loaded);

const loadingSelector = createSelector(stateSelector, (state) => state.loading);

export const getShippingEstimate: (state: any, selector: any) => any = createSelector(
    [byIdSelector, idSelector],
    (byId, id) => byId[id] || {}
);

const getLoadTimeForShippingEstimate = createSelector([loadedSelector, idSelector], (loaded, id) => loaded[id] || 0);

export const isShippingEstimateLoading: (state: any, selector: any) => any = createSelector(
    [loadingSelector, idSelector],
    (loading, id) => loading.includes(id)
);

const shouldFetchItemShippingEstimate = (state, itemId): boolean => {
    if (!itemId) {
        return false;
    }
    const item = getShippingEstimate(state, itemId);
    if (item) {
        const loaded = getLoadTimeForShippingEstimate(state, itemId);
        const time = Date.now();
        const diff = time - loaded;
        if (diff < REDUX_STORE_TIME) {
            return false;
        }
    }
    const loading = isShippingEstimateLoading(state, itemId);
    return !loading;
};

type ShippingInfoType = {
    countryCode: string;
    itemId: number;
    postalCode: string;
};

/* ACTION CREATORS */
const loadItemShippingEstimate =
    ({ countryCode, itemId, postalCode }: ShippingInfoType) =>
    async (dispatch: Function, getState: Function) => {
        try {
            const state = getState();
            const authToken = getAuthToken(state);
            const deployment = getDeployment(state);
            dispatch({
                payload: { itemId },
                type: LOAD_SHIPPING_ESTIMATE_REQUEST,
            });
            const response = await api.fetchItemShippingEstimate({
                authToken,
                // Arta expects GB for United Kingdom country code so we change it here.
                countryCode: countryCode === 'UK' ? 'GB' : countryCode,
                deployment,
                itemId,
                postalCode,
            });
            return dispatch({
                meta: { actionTime: Date.now(), itemId },
                payload: response.payload,
                type: LOAD_SHIPPING_ESTIMATE_SUCCESS,
            });
        } catch (error) {
            return dispatch({
                error: true,
                meta: { actionTime: Date.now(), itemId },
                payload: error,
                type: LOAD_SHIPPING_ESTIMATE_FAIL,
            });
        }
    };

/**
 * Fetches a new shipping estimate if one doesn't already exist for the given item or a past estimate is outdated
 */
export const fetchItemShippingEstimateIfNeeded =
    ({ countryCode, itemId, postalCode }: ShippingInfoType) =>
    async (dispatch: Function, getState: Function) => {
        if (shouldFetchItemShippingEstimate(getState(), itemId)) {
            return dispatch(loadItemShippingEstimate({ countryCode, itemId, postalCode }));
        }
        return Promise.resolve();
    };

/**
 * Force fetches a shipping estimate
 */
export const fetchItemShippingEstimate =
    ({ countryCode, itemId, postalCode }: ShippingInfoType) =>
    async (dispatch: Function) => {
        return dispatch(loadItemShippingEstimate({ countryCode, itemId, postalCode }));
    };
